import React, { Dispatch, useReducer } from 'react';

import { localStorage, StorageKey } from '@bimdata/client';

import handleError from '@/actions/common/handle-error.action';
import { getUsers } from '@/admin/api/common';
import { getAvatar } from '@/api/user';

import { State, Action } from "./types";
import { UserStatus } from '@bimdata/definitions/const';
import { AdminedUser } from '@/admin/types/admined-user.dto';

export const ROW_HEIGHT = 52;

export default (): [State, Dispatch<Action>] => {
  const [state, dispatch] = useReducer(
    (state: State, { type, payload }: Action): State => {
    let { list, listSize, pageSize, avatars, params, needStats } = state;
    switch (type) {
      case 'loadWithOffset': {
        if (listSize !== null && list.length >= listSize) {
          return state;
        }

        getUsers({
          ...(params.search ? { search: params.search } : {}),
          ...(params.statusFilter !== null ? { statusFilter: params.statusFilter } : {}),
          total: listSize === null,
          stats: needStats,
          offset: payload,
          limit: pageSize,
          sortBy: params.sorting[0],
          sortOrder: params.sorting[1] === 'A' ? 'ASC' : 'DESC',
        }).then(
          ({ page, total, stats }) => {
            if (typeof total === 'number') {
              dispatch({ type: 'setListSize', payload: total });
            }
            if (!!stats) {
              dispatch({ type: 'setStats', payload: stats });
            }
            dispatch({
              type: 'add',
              payload: {
                page,
                params,
                offset: payload
              }
            });
          }
        ).catch(
          handleError
        );

        return state;
      };
      case 'add': {
        if ( params.statusFilter !== payload.params.statusFilter
          || params.search !== payload.params.search
          || params.sorting[0] !== payload.params.sorting[0]
          || params.sorting[1] !== payload.params.sorting[1]
        ) {
          return state;
        }
        return {
          ...state,
          list: [
            ...(list || []).slice(0, payload.offset),
            ...payload.page,
          ],
        };
      }
      case 'setUsersStatus': {
        const { l, s } = list.reduce((acc, u) => {
          if (payload.ids.has(u.id)) {
            acc.s[u.status] && acc.s[u.status]!--;
            acc.s[payload.status] && acc.s[payload.status]!++;

            if (!params.statusFilter || params.statusFilter === payload.status) {
              acc.l.push({
              ...u,
              status: payload.status,
            });
            }
          } else {
            acc.l.push(u);
          }
          return acc;
        }, {
          l: new Array<AdminedUser>(),
          s: state.stats
        });
        return {
          ...state,
          list: l,
          needStats: true,
          stats: s
        };
      }case 'deleteUsers': {
        const { l, s } = list.reduce((acc, u) => {
          if (!payload.has(u.id)) {
            acc.l.push(u);
          } else {
            acc.s.total && acc.s.total--;
            acc.s[u.status] && acc.s[u.status]!--;
          }
          return acc;
        }, {
          l: new Array<AdminedUser>(),
          s: state.stats
        });
        const count = list.length - l.length;
        return {
          ...state,
          list: l,
          listSize: listSize && (listSize - count),
          needStats: true,
          stats: s
        };
      }
      case 'ensure': {
        const user = list?.find((u) => u.id === payload);
        if (!user) {
          break;
        }
        if ((typeof avatars[user.id] === 'undefined')) {
          let promise;
          if (user.avatar) {
            promise = getAvatar(user.id, user.avatar)
              .then((data) => dispatch({
                type: 'setAvatar',
                payload: {
                  uid: user.id,
                  data: data || false
                }
              }))
              .catch((error) => { handleError(error); });
          }
          avatars = {
            ...avatars,
            [user.id]: false
          };
        }
        return {
          ...state,
          avatars,
        };
      }
      case 'setAvatar': return {
        ...state,
        avatars: {
          ...avatars,
          [payload.uid]: payload.data,
        }
      };
      case 'setStatusFilter': {
        if (params.statusFilter === payload) {
          return state;
        }
        const newParams = {
          ...params,
          statusFilter: payload
        };
        localStorage.set(StorageKey.ADMIN_USERS_LIST_PARAMS, newParams);
        return {
          ...state,
          list: [],
          listSize: null,
          params: newParams
        };
      };
      case 'setSearch': {
        if (params.search === payload) {
          return state;
        }
        const newParams = {
          ...params,
          search: payload
        };
        localStorage.set(StorageKey.ADMIN_USERS_LIST_PARAMS, newParams);
        return {
          ...state,
          list: [],
          listSize: null,
          needStats: true,
          params: newParams
        };
      };
      case 'setSorting': {
        if (params.sorting[0] === payload[0] && params.sorting[1] === payload[1]) {
          return state;
        }
        const newParams = {
          ...params,
          sorting: payload
        };
        localStorage.set(StorageKey.ADMIN_USERS_LIST_PARAMS, newParams);
        return {
          ...state,
          list: [],
          listSize: null,
          params: newParams
        };
      };
      case 'setPageSize': {
        return {
          ...state,
          pageSize: Math.ceil(payload)
        };
      };
      case 'setListSize': {
        return {
          ...state,
          ...((payload < list.length) ? {
            list: [],
        } : {}),
          listSize: payload
        };
      };
      case 'setStats': {
        return {
          ...state,
          stats: payload,
          needStats: false,
        };
      };
    }
    return state;
  },
  {
    list: [],
    listSize: null,
    pageSize: Math.ceil(1.5 * window.innerHeight/ROW_HEIGHT),
    avatars: {},
    params: localStorage?.get(StorageKey.ADMIN_USERS_LIST_PARAMS) || {
      activityFilter: null,
      sorting: ['registered', 'D'],
      search: '',
    },
    stats: {
      total: null,
      [UserStatus.INACTIVE]: null,
      [UserStatus.ACTIVE]: null,
      [UserStatus.BLOCKED]: null,
      [UserStatus.GONE]: null,
    },
    needStats: true,
  });
  return [state, dispatch];
};

